<template>
  <div id="products" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-dialog
        v-if="imageUploadDialog"
        v-model="imageUploadDialog"
        max-width="700px"
        persistent
      >
        <i-draft-image-upload-dialog
          @close="imageUploadDialog = false"
          @completed="refreshCurrentTable"
        />
      </v-dialog>
      <v-dialog
        v-if="editDialog"
        v-model="editDialog"
        max-width="840px"
        persistent
        @click:outside="resetEditProduct"
        @keydown.esc="resetEditProduct"
      >
        <i-edit-product
          :submitting="submitting"
          :new-product="newProduct"
          :pricing-regions="pricingRegions"
          :us-tax-codes="usTaxCodes"
          :product="editedItem"
          :supplier-name="supplierName"
          :supplier-timezone="supplierTimezone"
          :units="units"
          @saveProduct="handleSaveProduct"
          @close="resetEditProduct()"
        ></i-edit-product>
      </v-dialog>

      <v-dialog
        v-if="publishingDialog"
        v-model="publishingDialog"
        width="390px"
        @click:outside="resetPublishingDialog"
        @keydown.esc="resetPublishingDialog"
      >
        <i-product-publishing-dialog
          :publishing="publishingProduct"
          :tenant-country-region="tenantCountryRegion"
          @confirm="submitPublishDate"
          @cancel="resetPublishingDialog"
        />
      </v-dialog>

      <v-dialog
        v-if="deletionDialog"
        v-model="deletionDialog"
        width="390px"
        @click:outside="resetDeletionDialog"
        @keydown.esc="resetDeletionDialog"
      >
        <i-product-deletion-dialog
          :deleting="deletingProduct"
          :product="selectedProducts[0]"
          @confirm="submitDeleteProduct"
          @cancel="resetDeletionDialog"
        />
      </v-dialog>

      <v-dialog
        v-if="archiveDeletionDialog"
        v-model="archiveDeletionDialog"
        width="390px"
        @click:outside="resetArchiveDeletionDialog"
        @keydown.esc="resetArchiveDeletionDialog"
      >
        <i-product-deletion-dialog
          :deleting="deletingArchivedProduct"
          :product="selectedProducts[0]"
          @confirm="submitDeleteArchivedProduct"
          @cancel="resetArchiveDeletionDialog"
        />
      </v-dialog>

      <v-dialog
        v-if="archivingDialog"
        v-model="archivingDialog"
        width="390px"
        @click:outside="resetArchivingDialog"
        @keydown.esc="resetArchivingDialog"
      >
        <i-product-archiving-dialog
          :archiving="archivingProduct"
          :product="selectedProducts[0]"
          @confirm="submitArchiveProduct"
          @cancel="resetArchivingDialog"
        />
      </v-dialog>

      <v-dialog
        v-if="toDraftDialog"
        v-model="toDraftDialog"
        width="390px"
        @click:outside="resetToDraftDialog"
        @keydown.esc="resetToDraftDialog"
      >
        <v-card>
          <v-card-title class="i-dialog-header-div">
            <span class="g-heading-m g-bold">Product Draft</span>
          </v-card-title>

          <v-card-text class="text-center py-6">
            <span>Product(s) will be immediately moved to Draft</span>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="i-dialog-actions-div justify-center">
            <v-btn
              rounded
              text
              class="i-btn-text-black"
              @click="resetToDraftDialog"
            >
              <strong>Cancel</strong>
            </v-btn>

            <v-btn
              rounded
              class="i-btn-block-secondary"
              :loading="unarchivingProduct"
              @click="submitToDraft"
            >
              <strong>Confirm</strong>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-card class="mt-4 px-8 py-6">
            <div class="text-center">
              To use this feature you must access your Supplier portal from a
              Desktop device
            </div>
            <div class="text-center">
              <v-btn
                rounded
                class="i-btn-block-secondary mt-4"
                :to="{ name: 'dashboard' }"
                >Dashboard</v-btn
              >
            </div>
          </v-card>
        </template>

        <template v-else>
          <v-row>
            <v-col cols="6" sm="4" md="2" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': productsStatus === LIVE }"
                @click="productsStatus = LIVE"
                >Live</v-btn
              >
            </v-col>

            <v-col cols="6" sm="4" md="2" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': productsStatus === LIVE_FUTURE
                }"
                @click="productsStatus = LIVE_FUTURE"
                >Upcoming</v-btn
              >
            </v-col>

            <v-col v-if="dataApprovalRequired" cols="6" sm="4" md="2" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': productsStatus === AWAITING_APPROVAL
                }"
                @click="productsStatus = AWAITING_APPROVAL"
              >
                <v-badge
                  color="accent"
                  :content="productsRequiringAmendmentsCount"
                  right
                  :value="productsRequiringAmendmentsCount"
                >
                  Awaiting Approval
                </v-badge>
              </v-btn>
            </v-col>

            <v-col cols="6" sm="4" md="2" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': productsStatus === DRAFT }"
                @click="productsStatus = DRAFT"
                >Draft</v-btn
              >
            </v-col>

            <v-col cols="6" sm="4" md="2" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': productsStatus === ARCHIVE }"
                @click="productsStatus = ARCHIVE"
                >Archive</v-btn
              >
            </v-col>

            <v-spacer></v-spacer>

            <v-col cols="12" sm="4" md="2" lg="2">
              <i-instructions
                instructionKey="data-maintenance/products"
                :instructionSection="instructionSection"
                :disregardSections="
                  !dataApprovalRequired ? ['Awaiting Approval Products'] : []
                "
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <section class="products-workspace">
                <i-products-command-rail
                  :loading="loading"
                  :show-import-actions="productsStatus === LIVE"
                  :show-image-upload="productsStatus === DRAFT"
                  :image-upload-disabled="!canUploadDraftImages"
                  @upload-images="
                    canUploadDraftImages && (imageUploadDialog = true)
                  "
                  :search="search"
                  :brands="brandFilterOptions"
                  :categories="categoryFilterOptions"
                  :selected-brands="selectedBrands"
                  :selected-categories="selectedCategories"
                  @add-product="productEventHandlers.addProduct"
                  @update:search="search = $event"
                  @update:selected-brands="selectedBrands = $event"
                  @update:selected-categories="selectedCategories = $event"
                  @refresh="refreshCurrentTable"
                />

                <i-products-live
                  v-if="productsStatus === LIVE"
                  :loading="loading"
                  :pageSize="pageSize"
                  :products="filteredProductsByStatus"
                  :pricing-regions="pricingRegions"
                  v-model:search="search"
                  :supplierItemId="supplierItemId"
                  :tableHeight="tableHeight"
                  :updatingAvailability="updatingAvailability"
                  :addProductHandler="openAddProductDialogHandler"
                  @add-product="productEventHandlers.addProduct"
                  @archive-product="productEventHandlers.archiveProduct"
                  @edit-product="productEventHandlers.editProduct"
                  @update-product-availability="
                    productEventHandlers.updateProductAvailability
                  "
                />

                <i-products-live-future
                  v-if="productsStatus === LIVE_FUTURE"
                  :loading="loading"
                  :pageSize="pageSize"
                  :products="filteredProductsByStatus"
                  :pricing-regions="pricingRegions"
                  v-model:search="search"
                  :supplierItemId="supplierItemId"
                  :tableHeight="tableHeight"
                  :addProductHandler="openAddProductDialogHandler"
                  @add-product="productEventHandlers.addProduct"
                  @delete-product="productEventHandlers.deleteProduct"
                  @edit-product="productEventHandlers.editProduct"
                  @edit-publish-date="productEventHandlers.editPublishDate"
                />

                <i-products-awaiting-approval
                  v-if="
                    dataApprovalRequired && productsStatus === AWAITING_APPROVAL
                  "
                  :loading="loading"
                  :pageSize="pageSize"
                  :products="filteredProductsByStatus"
                  :pricing-regions="pricingRegions"
                  v-model:search="search"
                  :supplierItemId="supplierItemId"
                  :tableHeight="tableHeight"
                  :addProductHandler="openAddProductDialogHandler"
                  @add-product="productEventHandlers.addProduct"
                  @delete-product="productEventHandlers.deleteProduct"
                  @edit-product="productEventHandlers.editProduct"
                  @edit-publish-date="productEventHandlers.editPublishDate"
                />

                <i-products-draft
                  v-if="productsStatus === DRAFT"
                  :loading="loading"
                  :pageSize="pageSize"
                  :products="filteredProductsByStatus"
                  :pricing-regions="pricingRegions"
                  v-model:search="search"
                  :supplierItemId="supplierItemId"
                  :tableHeight="tableHeight"
                  :addProductHandler="openAddProductDialogHandler"
                  @add-product="productEventHandlers.addProduct"
                  @delete-product="productEventHandlers.deleteProduct"
                  @edit-product="productEventHandlers.editProduct"
                  @edit-publish-date="productEventHandlers.editPublishDate"
                />

                <i-products-archive
                  v-if="productsStatus === ARCHIVE"
                  :loading="loading"
                  :pageSize="pageSize"
                  :products="filteredProductsByStatus"
                  :pricing-regions="pricingRegions"
                  v-model:search="search"
                  :supplierItemId="supplierItemId"
                  :tableHeight="tableHeight"
                  :addProductHandler="openAddProductDialogHandler"
                  @add-product="productEventHandlers.addProduct"
                  @delete-archived-product="
                    productEventHandlers.deleteArchivedProduct
                  "
                  @edit-product="productEventHandlers.editProduct"
                  @move-to-draft="productEventHandlers.moveToDraft"
                />
              </section>

              <i-paginator
                v-if="pagination.totalPages > 1"
                class="mt-6"
                :currentPage="currentPage"
                :pageSize="paginationPageSize"
                :pagination="pagination"
                @paginate="handlePageChange"
              />
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import Bugsnag from '@bugsnag/js'
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  createProduct,
  updateProduct,
  updateUpcomingProduct,
  deleteUpcomingProduct,
  deleteArchivedProductPermanently,
  editProductAvailability,
  uploadImage,
  fetchUnits,
  fetchDietaryInformation,
  submitProductArchive,
  submitProductUnarchive,
  submitPublishDate,
  fetchUsTaxCodes,
  fetchProductFilterOptions,
  fetchProductsFromBackend
} from '@/services/products'
import DataTables from '@/utils/DataTables'
import iEditProduct from '../components/products/EditProduct'
import iDraftImageUploadDialog from '../components/dialogs/DraftImageUploadDialog'
import iProductArchivingDialog from '../components/dialogs/ProductArchivingDialog'
import iProductDeletionDialog from '../components/dialogs/ProductDeletionDialog'
import iProductPublishingDialog from '../components/dialogs/ProductPublishingDialog'
import iProductsLive from '../components/products/ProductsLive'
import iProductsLiveFuture from '../components/products/ProductsLiveFuture'
import iProductsAwaitingApproval from '../components/products/ProductsAwaitingApproval'
import iProductsDraft from '../components/products/ProductsDraft'
import iProductsArchive from '../components/products/ProductsArchive'
import iProductsCommandRail from '../components/products/ProductsCommandRail'
import iInstructions from '@/components/Instructions'
import iPaginator from '@/components/Paginator'

const PRODUCT_STATUSES = {
  LIVE: 'LIVE',
  LIVE_FUTURE: 'LIVE_FUTURE',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  DRAFT: 'DRAFT',
  ARCHIVE: 'ARCHIVE'
}

const TIMEOUT_DURATION = 250
const VISIBLE_TABLE_ROWS = 10
const PRODUCTS_PAGE_SIZE = 25

export default {
  compatConfig: {
    WATCH_ARRAY: false
  },

  components: {
    iDraftImageUploadDialog,
    iInstructions,
    iPaginator,
    iEditProduct,
    iProductArchivingDialog,
    iProductDeletionDialog,
    iProductPublishingDialog,
    iProductsLive,
    iProductsLiveFuture,
    iProductsAwaitingApproval,
    iProductsDraft,
    iProductsArchive,
    iProductsCommandRail
  },

  data: () => ({
    imageUploadDialog: false,
    draftImageUploadAvailability: null,
    draftImageUploadRequest: 0,
    newProduct: false,
    archivingProduct: false,
    deletingProduct: false,
    deletingArchivedProduct: false,
    publishingProduct: false,
    unarchivingProduct: false,
    deletionDialog: false,
    archiveDeletionDialog: false,
    archivingDialog: false,
    editDialog: false,
    publishingDialog: false,
    toDraftDialog: false,
    currentPage: 1,
    updatingAvailability: false,
    editedItem: undefined,
    pageSize: -1,
    paginationPageSize: PRODUCTS_PAGE_SIZE,
    productsStatus: PRODUCT_STATUSES.LIVE,
    selectedProducts: [],
    submitting: false,
    tableHeight: '0',
    tableTimeout: null,
    units: [],
    usTaxCodes: [],
    referenceDietaryInformation: [],
    productEventHandlers: {},
    openAddProductDialogHandler: () => {},
    search: '',
    selectedBrands: [],
    selectedCategories: [],
    productFilterOptions: { brands: [], categories: [] },
    ...PRODUCT_STATUSES
  }),

  props: {
    supplierItemId: String,
    status: String
  },

  computed: {
    ...mapGetters('profile', ['readyToTrade', 'dataApprovalRequired']),
    ...mapGetters('products', ['liveProducts']),
    ...mapGetters('dataMaintenance', ['productsRequiringAmendmentsCount']),
    ...mapGetters('tenant', ['tenantCountryRegion']),

    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('profile', ['info']),
    ...mapState('pricingRegions', ['pricingRegions']),
    ...mapState('products', ['products', 'loading', 'pagination']),

    canUploadDraftImages() {
      return (
        this.productsStatus === PRODUCT_STATUSES.DRAFT &&
        this.draftImageUploadAvailability?.supplierId ===
          this.selectedSupplierId &&
        this.draftImageUploadAvailability?.available === true
      )
    },

    instructionSection() {
      let view = null
      switch (this.productsStatus) {
        case PRODUCT_STATUSES.LIVE:
          view = 'Live'
          break
        case PRODUCT_STATUSES.LIVE_FUTURE:
          view = 'Upcoming'
          break
        case PRODUCT_STATUSES.AWAITING_APPROVAL:
          view = 'Awaiting Approval'
          break
        case PRODUCT_STATUSES.DRAFT:
          view = 'Draft'
          break
        case PRODUCT_STATUSES.ARCHIVE:
          view = 'Archive'
          break
      }
      return view
    },

    isOverMaximumVisibleRows() {
      return this.filteredProductsByStatus.length > VISIBLE_TABLE_ROWS
    },

    productsByStatus() {
      return this.products
        .filter((p) => p.status === this.productsStatus)
        .sort((p1, p2) => (p1.shortDescription > p2.shortDescription ? 1 : -1))
    },

    brandFilterOptions() {
      return this.productFilterOptions.brands
    },

    categoryFilterOptions() {
      return this.productFilterOptions.categories
    },

    filteredProductsByStatus() {
      return this.productsByStatus.filter((product) => {
        const category = product.categoryProductHierarchy?.category
        const matchesBrand =
          !this.selectedBrands.length ||
          this.selectedBrands.includes(product.brand)
        const matchesCategory =
          !this.selectedCategories.length ||
          this.selectedCategories.includes(category)

        return matchesBrand && matchesCategory
      })
    },

    supplierName() {
      return this.info?.name
    },

    supplierTimezone() {
      return this.info?.timezone
    }
  },

  methods: {
    ...mapActions('products', [
      'fetchProducts',
      'refreshProductsForSupplierEvent',
      'upsertProductLocally',
      'removeProductsLocally'
    ]),
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('dataMaintenance', ['fetchApprovalStatusStatistics']),

    // ***
    // Helper functions
    // ***

    isExistingProduct(item) {
      return item?.productCode
    },

    isDraftProduct(item) {
      return item.status
        ? item.status === PRODUCT_STATUSES.DRAFT
        : [PRODUCT_STATUSES.DRAFT].includes(this.getProductStatus(item))
    },

    isUpcomingProduct(item) {
      return [
        PRODUCT_STATUSES.DRAFT,
        PRODUCT_STATUSES.LIVE_FUTURE,
        PRODUCT_STATUSES.AWAITING_APPROVAL
      ].includes(this.getProductStatus(item))
    },

    getProductStatus(item) {
      return this.productsByStatus.find(
        (product) => product.productCode === item.productCode
      ).status
    },

    notifyApiError(payload) {
      const { error, defaultError } = payload

      if (!error) {
        this.addNotification({ color: 'error', defaultError: true })
        return false
      }

      Bugsnag.notify(error)

      let errorMessage
      let abortCurrentEdit
      switch (error.response?.data?.code || 'UNKNOWN_ERROR') {
        case 'ARCHIVE_PRODUCT_VIOLATES_MIN_FAMILY_GROUP_MEMBERS':
          errorMessage =
            'Product is in a Family Group with only 2 products (must have 2 or greater). Delete the Family Group to Archive this product.'
          abortCurrentEdit = false
          break
        case 'ITEM_CODE_ALREADY_EXISTS':
          errorMessage = error.response?.data?.error
          abortCurrentEdit = false
          break
        case 'EDIT_PRODUCT_PUBLISH_DATE_CONFLICTS_WITH_PROMOTIONS':
          errorMessage =
            'Active or Upcoming Promotion(s) exist for this product. Price related & carton qty edits can only be made when ALL affected promotions have ended or been deleted.'
          abortCurrentEdit = true
          break
        default:
          errorMessage = defaultError
          abortCurrentEdit = false
      }

      this.addNotification({ color: 'error', text: errorMessage, timeout: -1 })
      return abortCurrentEdit
    },

    setTableSize() {
      if (!this.isOverMaximumVisibleRows) {
        this.tableHeight = 'auto'
        return
      }

      // Timeout needed to let UI fully render before calculating values
      clearTimeout(this.tableTimeout)
      this.tableTimeout = setTimeout(() => {
        this.tableHeight = DataTables.calculateTableHeight(VISIBLE_TABLE_ROWS)
      }, TIMEOUT_DURATION)
    },

    wasOrIsProductLive(product) {
      return product?.wasOrIsLive
    },

    normalizeReloadOptions(forceOrOptions = false) {
      if (
        forceOrOptions &&
        typeof forceOrOptions === 'object' &&
        !Array.isArray(forceOrOptions)
      ) {
        return {
          force: !!forceOrOptions.force,
          background: !!forceOrOptions.background
        }
      }

      return {
        force: !!forceOrOptions,
        background: false
      }
    },

    refreshProductsInBackground(force = true) {
      return Promise.all([
        this.reload({ force, background: true }),
        this.loadProductFilterOptions()
      ]).catch(() => {})
    },

    refreshSupplierProductCachesInBackground() {
      return Promise.all([
        this.refreshProductsForSupplierEvent({
          supplierId: this.selectedSupplierId
        }),
        this.loadProductFilterOptions()
      ]).catch(() => {})
    },

    removeProductsFromCurrentPage(productCodes = []) {
      if (!productCodes.length) return

      this.removeProductsLocally({
        productCodes,
        pageSize: this.paginationPageSize
      })
    },

    getSelectedProductCode(product) {
      return typeof product === 'string' ? product : product?.productCode
    },

    canPatchProductLocally(product) {
      if (!product?.productCode || this.search) return false
      return this.products.some(
        (existingProduct) => existingProduct.productCode === product.productCode
      )
    },

    applySavedProductLocally(
      product,
      { removedFromCurrentStatus = false } = {}
    ) {
      if (!product?.productCode) return

      if (
        removedFromCurrentStatus ||
        product.status !== this.productsStatus ||
        this.search
      ) {
        this.removeProductsFromCurrentPage([product.productCode])
        return
      }

      if (this.canPatchProductLocally(product)) {
        this.upsertProductLocally({ product })
      }
    },

    // ***
    // Functions related to handling product actions
    // ***

    generateProductDefaultData() {
      return {
        allergens: '',
        baseUnitGtin: '',
        brand: '',
        categoryProductHierarchy: {
          category: '',
          channel: 'SUPERMARKETS',
          subCategory: '',
          tradingDepartment: ''
        },
        clearance: '',
        countryOfOrigin: '',
        dietaryInformation: _.cloneDeep(this.referenceDietaryInformation) || {},
        gtin: '',
        hasGst: true,
        hasWet: false,
        usTaxCategoryId: null,
        imageTypesToDelete: [],
        images: [],
        ingredients: '',
        liquorInformation: {
          alcoholVolume: '',
          bottledIn: '',
          brewery: '',
          closureType: '',
          regionOfOrigin: '',
          standardDrinks: '',
          stateOfOrigin: '',
          style: '',
          type: '',
          varietal: '',
          vintage: '',
          wineBody: '',
          wineMaker: '',
          wineSweetness: '',
          awards: {
            award1: {
              ratedBy: '',
              rating: ''
            },
            award2: {
              ratedBy: '',
              rating: ''
            },
            award3: {
              ratedBy: '',
              rating: ''
            },
            award4: {
              ratedBy: '',
              rating: ''
            }
          }
        },
        packageQuantity: '',
        productType: '',
        publishAtDate: '',
        rangeRule: {
          targetBannerGroupIds: [],
          targetMultiStoreOperatorIds: [],
          targetStoreIds: [],
          targetStorePricingRegions: []
        },
        regionalPrices: [],
        shortDescription: '',
        supplierItemId: '',
        supplierKeywords: [],
        availabilityStates: [],
        tradeDescription: '',
        unit: '',
        unitQty: '',
        variableWeightTargetInKg: '',
        soldAs: ''
      }
    },

    handleAddProduct() {
      this.editedItem = _.cloneDeep({ ...this.generateProductDefaultData() })
      this.newProduct = true
      this.editDialog = true
    },

    handleArchiveProduct(item) {
      this.selectedProducts[0] = item
      this.archivingDialog = true
    },

    handleEditProduct(item) {
      if (!item) throw new Error('No product to be edited')

      const { regionalPriceMap, ...product } = item
      const regionalPrices = Object.keys(regionalPriceMap).map(
        (pricingRegionCode) => {
          const {
            fromDateTime,
            id,
            price,
            rrp,
            grossProfitPercentage,
            unitPriceIncTax,
            unitPriceExGst,
            pricePerKgIncTax,
            pricePerKgExGst
          } = regionalPriceMap[pricingRegionCode][0]

          return {
            fromDateTime,
            grossProfitPercentage,
            id,
            price,
            pricingRegionCode,
            rrp,
            unitPriceIncTax,
            unitPriceExGst,
            pricePerKgIncTax,
            pricePerKgExGst
          }
        }
      )

      product.soldAs = product.variableWeightTargetInKg
        ? 'VARIABLE_WEIGHT'
        : product.packageType === 'PALLET'
          ? 'PALLET'
          : 'FIXED_WEIGHT'

      // Bulk uploaded products don't have images property, need to added it manually
      if (product.images === undefined) product.images = []

      this.editedItem = _.cloneDeep({
        ...this.generateProductDefaultData(),
        ...product,
        usTaxCategoryId: product.usTaxCode?.usTaxCategoryId || null,
        regionalPrices
      })

      this.newProduct = false
      this.editDialog = true
    },

    handleDeleteProduct(item) {
      this.selectedProducts[0] = item
      this.deletionDialog = true
    },

    handleDeleteArchivedProduct(item) {
      this.selectedProducts[0] = item
      this.archiveDeletionDialog = true
    },

    handleMoveToDraft(items) {
      this.selectedProducts = [...items]
      this.toDraftDialog = true
    },

    handlePublishDate(items) {
      this.selectedProducts = [...items]
      this.publishingDialog = true
    },

    // ***
    // Functions related to closing dialogs
    // ***

    resetEditProduct() {
      this.editDialog = false
      this.editedItem = undefined
    },

    resetPublishingDialog() {
      this.publishingProduct = this.publishingDialog = false
    },

    resetArchivingDialog() {
      this.archivingProduct = this.archivingDialog = false
      this.selectedProducts = []
    },

    resetDeletionDialog() {
      this.deletingProduct = this.deletionDialog = false
      this.selectedProducts = []
    },

    resetArchiveDeletionDialog() {
      this.deletingArchivedProduct = this.archiveDeletionDialog = false
      this.selectedProducts = []
    },

    resetToDraftDialog() {
      this.unarchivingProduct = this.toDraftDialog = false
    },

    // ***
    // Functions related to executing product actions
    // ***

    updateProductAvailability(payload) {
      this.updatingAvailability = true
      const { productCode, availabilityStates } = payload

      editProductAvailability(this.selectedSupplierId, productCode, {
        availabilityStates
      })
        .then(() => {
          this.updatingAvailability = false

          this.addNotification({
            color: 'secondary',
            text: 'Product availability updated!'
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Product availability could not be updated, please try again...'
          })
        })
    },

    async reload(forceOrOptions = false) {
      const { force, background } = this.normalizeReloadOptions(forceOrOptions)
      const supplierId = this.selectedSupplierId
      const status = this.productsStatus
      const draftRequest = (this.draftImageUploadRequest =
        (this.draftImageUploadRequest || 0) + 1)
      this.draftImageUploadAvailability = null
      const response = await this.fetchProducts({
        supplierId,
        force,
        background,
        status,
        page: this.currentPage - 1,
        size: this.paginationPageSize,
        query: this.search,
        brands: this.selectedBrands,
        categories: this.selectedCategories
      })

      if (
        this.pagination.totalPages > 0 &&
        this.currentPage > this.pagination.totalPages
      ) {
        this.currentPage = this.pagination.totalPages
        return this.reload(force)
      }

      if (
        status === PRODUCT_STATUSES.DRAFT &&
        this.productsStatus === status &&
        draftRequest === this.draftImageUploadRequest
      ) {
        let available = this.pagination.total > 0
        // Uploads match all drafts, including products outside the current filters.
        if (
          !available &&
          (this.search ||
            this.selectedBrands.length ||
            this.selectedCategories.length)
        ) {
          const unfiltered = await fetchProductsFromBackend(
            supplierId,
            PRODUCT_STATUSES.DRAFT,
            0,
            1
          ).catch(() => null)
          available = Array.isArray(unfiltered)
            ? unfiltered.length > 0
            : unfiltered?.pagination?.total > 0
        }
        if (
          draftRequest === this.draftImageUploadRequest &&
          supplierId === this.selectedSupplierId &&
          this.productsStatus === PRODUCT_STATUSES.DRAFT
        ) {
          this.draftImageUploadAvailability = { supplierId, available }
        }
      }

      this.fetchApprovalStatusStatistics().catch(() => {})

      return response
    },

    async loadProductFilterOptions() {
      this.productFilterOptions = await fetchProductFilterOptions(
        this.selectedSupplierId,
        this.productsStatus
      )
    },

    async refreshCurrentTable() {
      await Promise.all([
        this.reload({ force: true }),
        this.loadProductFilterOptions()
      ])
    },

    async handlePageChange(page) {
      this.currentPage = page
      await this.reload()
    },

    submitArchiveProduct(product) {
      this.archivingProduct = true

      submitProductArchive(this.selectedSupplierId, [product.productCode])
        .then(() => {
          this.removeProductsFromCurrentPage([product.productCode])
          this.refreshSupplierProductCachesInBackground()
          this.addNotification({
            color: 'secondary',
            text: 'Product successfully submitted for archiving!'
          })
        })
        .catch((error) => {
          this.notifyApiError({
            error,
            defaultError:
              'Product could not be submitted for archiving, please try again...'
          })
        })
        .finally(() => this.resetArchivingDialog())
    },

    submitDeleteProduct(product) {
      this.deletingProduct = true

      const actionMessage =
        this.isDraftProduct(product) && this.wasOrIsProductLive(product)
          ? 'Archived'
          : 'Deleted'

      deleteUpcomingProduct(this.selectedSupplierId, product.productCode)
        .then(() => {
          this.removeProductsFromCurrentPage([product.productCode])
          this.refreshSupplierProductCachesInBackground()
          this.addNotification({
            color: 'secondary',
            text: `Product successfully ${actionMessage}!`
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: `Product could not be ${actionMessage}, please try again...`
          })
        })
        .finally(() => this.resetDeletionDialog())
    },

    submitDeleteArchivedProduct(product) {
      this.deletingArchivedProduct = true

      deleteArchivedProductPermanently(
        this.selectedSupplierId,
        product.productCode
      )
        .then(() => {
          this.removeProductsFromCurrentPage([product.productCode])
          this.refreshSupplierProductCachesInBackground()
          this.addNotification({
            color: 'secondary',
            text: 'Product permanently deleted!'
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Product could not be permanently deleted, please try again...'
          })
        })
        .finally(() => this.resetArchiveDeletionDialog())
    },

    submitToDraft() {
      this.unarchivingProduct = true

      submitProductUnarchive(this.selectedSupplierId, this.selectedProducts)
        .then(() => {
          const productCodes = this.selectedProducts.map(
            (product) => product.productCode
          )
          this.removeProductsFromCurrentPage(productCodes)
          this.selectedProducts = []
          this.refreshSupplierProductCachesInBackground()
          this.addNotification({
            color: 'secondary',
            text: 'Product(s) successfully submitted to draft!'
          })
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Product(s) could not be submitted, please try again...'
          })
        })
        .finally(() => this.resetToDraftDialog())
    },

    submitPublishDate(publishDate) {
      this.publishingProduct = true

      submitPublishDate(
        this.selectedSupplierId,
        this.selectedProducts,
        publishDate
      )
        .then(() => {
          const productCodes = this.selectedProducts
            .map(this.getSelectedProductCode)
            .filter(Boolean)
          this.removeProductsFromCurrentPage(productCodes)
          this.selectedProducts = []
          this.refreshSupplierProductCachesInBackground()
          this.addNotification({
            color: 'secondary',
            text: 'Product(s) Publish Date updated!'
          })
        })
        .catch((error) => {
          let errorMessage =
            'Product(s) Publish Date update failed, please try again...'
          if (
            error.response?.data?.code ===
            'EDIT_PRODUCT_PUBLISH_DATE_CONFLICTS_WITH_PROMOTIONS'
          ) {
            errorMessage =
              'Active or Upcoming Promotion(s) exist for for at least one of the selected Product(s)). Publish date can only be changed when ALL affected promotions have ended or been deleted.'
          }
          this.addNotification({
            color: 'error',
            text: errorMessage,
            timeout: 9000
          })
        })
        .finally(() => this.resetPublishingDialog())
    },

    // ***
    // Functions related to saving products
    // ***

    async handleSaveProduct(item, images) {
      if (!item) throw new Error('No product to be saved')

      this.submitting = true
      this.addNotification({
        color: 'secondary',
        text: 'Saving your product...'
      })

      try {
        const savedProduct = await this.processProductChanges(item)
        await this.processProductImages(item, images)
        const removedFromCurrentStatus =
          await this.processProductPublishDate(item)

        this.applySavedProductLocally(savedProduct || item, {
          removedFromCurrentStatus
        })
        this.refreshProductsInBackground()
        this.addNotification({
          color: 'secondary',
          text: 'Your product list has been updated!'
        })
        this.resetEditProduct()

        // For when is new Supplier and has not finished setup steps
        if (!this.readyToTrade)
          await this.$router.push({
            name: 'dashboard',
            query: { showTradingSteps: 'true' }
          })
      } catch (error) {
        const abortCurrentEdit = this.notifyApiError({
          error,
          defaultError: 'Something went wrong. Please try again...'
        })
        if (abortCurrentEdit) {
          this.resetEditProduct()
        }
      } finally {
        this.submitting = false
      }
    },

    async processProductChanges(item) {
      if (this.isExistingProduct(item)) {
        if (this.isUpcomingProduct(item)) {
          return updateUpcomingProduct(
            this.selectedSupplierId,
            item.productCode,
            item
          )
        } else {
          return updateProduct(this.selectedSupplierId, item.productCode, item)
        }
      } else {
        const res = await createProduct(this.selectedSupplierId, item)
        item.productCode = res.productCode
        item.status = res.status
        return res
      }
    },

    /*
      We need to upload each image in sequence to avoid
      issues with concurrent requests trying to update
      the base upcoming product object in the backend
    */
    async processProductImages(item, images) {
      for (const image of images) {
        if (image.image && image.type) {
          await uploadImage(
            item.productCode,
            image.image,
            image.type,
            !this.newProduct
          )
        }
      }
    },

    /*
      Call submitPublishDate API required for DRAFT products
      to make sure backend can validate product is publishable
    */
    async processProductPublishDate(item) {
      if (item.publishAtDate && this.isDraftProduct(item)) {
        await submitPublishDate(
          this.selectedSupplierId,
          [item.productCode],
          item.publishAtDate
        )
        return true
      }

      return false
    }
  },

  async mounted() {
    try {
      if (this.status) this.productsStatus = this.status
      const [, , referenceDietaryInformation, units, usTaxCodes] =
        await Promise.all([
          this.reload(),
          this.loadProductFilterOptions(),
          fetchDietaryInformation(),
          fetchUnits(),
          fetchUsTaxCodes()
        ])
      this.referenceDietaryInformation = referenceDietaryInformation
      this.units = units
      this.usTaxCodes = usTaxCodes
    } catch {
      this.addNotification({
        color: 'error',
        text: 'Products data could not be loaded, please try again...'
      })
    }
  },

  watch: {
    async productsStatus(newValue, oldValue) {
      this.selectedProducts = []
      if (oldValue && newValue !== oldValue) {
        const hadSearch = !!this.search
        this.search = ''
        this.currentPage = 1
        await this.loadProductFilterOptions()
        if (hadSearch) return
        await this.reload()
      }
    },

    selectedBrands() {
      this.currentPage = 1
      this.debouncedReload()
    },

    selectedCategories() {
      this.currentPage = 1
      this.debouncedReload()
    },

    search() {
      this.currentPage = 1
      this.debouncedReload()
    },

    filteredProductsByStatus: {
      deep: true,
      handler() {
        this.setTableSize()
      }
    }
  },

  created() {
    this.openAddProductDialogHandler = () => {
      this.editedItem = _.cloneDeep({ ...this.generateProductDefaultData() })
      this.newProduct = true
      this.editDialog = true
    }
    this.productEventHandlers = {
      addProduct: this.openAddProductDialogHandler,
      archiveProduct: (item) => this.handleArchiveProduct(item),
      deleteArchivedProduct: (item) => this.handleDeleteArchivedProduct(item),
      deleteProduct: (item) => this.handleDeleteProduct(item),
      editProduct: (item) => this.handleEditProduct(item),
      editPublishDate: (products) => this.handlePublishDate(products),
      moveToDraft: (products) => this.handleMoveToDraft(products),
      updateProductAvailability: (payload) =>
        this.updateProductAvailability(payload)
    }

    this.debouncedReload = _.debounce(() => {
      this.reload()
    }, 250)
  },

  beforeUnmount() {
    this.debouncedReload?.cancel?.()
  }
}
</script>

<style lang="scss" scoped>
#products {
  .products-workspace {
    overflow: hidden;
    background: #fff;
    border: 1px solid #dde1e8;
    border-radius: 6px;
    box-shadow: 0 2px 6px rgba(27, 36, 48, 0.06);
  }

  :deep(.products-workspace .i-data-table) {
    box-shadow: none !important;
  }

  :deep(.p-add-product-click-target) {
    display: block;
  }

  .i-btn-select.v-btn {
    color: rgba(0, 0, 0, 0.87) !important;
    font-size: 14px;
    line-height: 21px;
  }

  .i-btn-instructions.v-btn.v-btn--size-large {
    font-size: 14px;
    line-height: 21px;
  }
}
</style>
